<template>
  <section class="service-page" dir="rtl">
    <div v-if="loading" class="page-loading">
      <div class="loader-card">
        <div class="loader-ring"></div>
        <h3>جاري تحميل بيانات المرافق الصحية</h3>
        <p>يرجى الانتظار...</p>
      </div>
    </div>

    <div class="page-header">
      <h1>محور تقديم الخدمات</h1>
      <p>
        يعكس مباشرة الأثر على وصول السكان إلى الخدمة الصحية، ويجمع بين توفر
        الخدمات واستمراريتها وشمولية حزمة الرعاية.
      </p>
    </div>

    <div class="top-row">
      <div class="counter">{{ facilities.length }} مرفق</div>
      <select>
        <option>محور تقديم الخدمات</option>
      </select>
    </div>

    <!-- مؤشرات المحور -->
    <div class="axis-summary">
      <div class="axis-card-header">
        <div>
          <h2>تقديم الخدمات <small>(SRV)</small></h2>
          <p>توفر الخدمات الصحية واستمراريتها وشمولية حزمة الرعاية.</p>
        </div>

        <div class="axis-score">
          <span>30%</span>
          <small>وزن FIS</small>
        </div>
      </div>

      <div class="axis-stats">
        <div>
          <b>{{ serviceKpis.length }}</b>
          <span>عدد المؤشرات</span>
        </div>

        <div>
          <b>{{ facilities.length }}</b>
          <span>العينة / مرافق</span>
        </div>

        <div>
          <b>{{ serviceCriticalCount }}</b>
          <span>مؤشرات حرجة</span>
        </div>
      </div>
    </div>

    <div class="service-kpi-grid">
      <article
        v-for="kpi in serviceKpis"
        :key="kpi.code"
        class="service-kpi-card"
        :class="{ flag: kpi.flag && kpiStatus(kpi) !== 'g' }"
      >
        <div class="service-kpi-top">
          <span class="code">{{ kpi.code }}</span>
          <span v-if="kpi.composite" class="composite">مركّب</span>
        </div>

        <h3>{{ kpi.name }}</h3>

        <div class="service-kpi-value">
          <strong :style="{ color: statusColor(kpi) }">{{ kpi.val }}</strong>
          <span>{{ kpi.unit }}</span>
          <em>المعيار {{ kpi.dir === "lt" ? "≤" : "≥" }} {{ kpi.std }}</em>
        </div>

        <div class="gauge">
          <div
            :style="{
              width: barWidth(kpi) + '%',
              background: statusColor(kpi),
            }"
          ></div>
        </div>

        <div class="service-kpi-foot">
          <span>
            الإنجاز:
            <b :style="{ color: statusColor(kpi) }">{{ achieve(kpi) }}%</b>
          </span>

          <span :class="['rag', kpiStatus(kpi)]">
            {{ statusLabel[kpiStatus(kpi)] }}
          </span>
        </div>

        <div v-if="kpi.chart" class="axis-mini-chart">
          <h4>{{ kpi.chart.label }}</h4>
          <apexchart
            :type="kpiApex(kpi).type"
            height="180"
            :series="kpiApex(kpi).series"
            :options="kpiApex(kpi).options"
          />
        </div>
      </article>
    </div>

    <div class="axis-card">
      <div v-if="facilities.length" class="charts-grid">
        <div class="chart-card">
          <div class="section-title">الخدمات المتوقفة حسب المرفق</div>
          <apexchart
            type="bar"
            height="320"
            :series="[
              {
                name: 'الخدمات المتوقفة',
                data: facilities.map((x) => x.interrupted_services),
              },
            ]"
            :options="barOptions(facilities.map((x) => x.facility_name))"
          />
        </div>

        <div class="chart-card">
          <div class="section-title">الزيارات قبل/بعد الأزمة حسب المرفق</div>
          <apexchart
            type="bar"
            height="320"
            :series="facilityVisitsSeries"
            :options="barOptions(facilities.map((x) => x.facility_name))"
          />
        </div>

        <div class="chart-card">
          <div class="section-title">توزيع مستوى الخطورة</div>
          <apexchart
            v-if="riskLevelSeries.length"
            type="donut"
            height="330"
            :series="riskLevelSeries"
            :options="donutOptions(riskLevelLabels)"
          />
          <div v-else class="empty-chart">لا توجد بيانات</div>
        </div>

        <div class="chart-card">
          <div class="section-title">توزيع حالات الخدمات</div>
          <apexchart
            v-if="statusSeries.length"
            type="donut"
            height="330"
            :series="statusSeries"
            :options="donutOptions(statusLabels)"
          />
          <div v-else class="empty-chart">لا توجد بيانات</div>
        </div>
      </div>

      <div class="table-wrap indicators-table">
        <div class="table-title">
          إطار المؤشرات الكامل
          <span>SRV-01 إلى SRV-08</span>
        </div>

        <table>
          <thead>
            <tr>
              <th>الكود</th>
              <th>المؤشر</th>
              <th>صيغة الاحتساب</th>
              <th>الوحدة</th>
              <th>المعيار المرجعي</th>
            </tr>
          </thead>

          <tbody>
            <tr v-for="item in serviceIndicators" :key="item.code">
              <td class="code">{{ item.code }}</td>
              <td class="name">{{ item.name }}</td>
              <td>{{ item.formula }}</td>
              <td class="unit">{{ item.unit }}</td>
              <td>{{ item.standard }}</td>
            </tr>
          </tbody>
        </table>
      </div>

      <div class="filter-box">
        <input
          v-model="filters.search"
          type="text"
          placeholder="بحث باسم المرفق..."
        />

        <select v-model="filters.risk">
          <option :value="null">كل مستويات الخطورة</option>
          <option v-for="item in riskItems" :key="item" :value="item">
            {{ item }}
          </option>
        </select>
      </div>

      <div class="services-table-wrap">
        <div class="table-title">
          تفاصيل المرافق الصحية
          <span>{{ filteredFacilities.length }} مرفق</span>
        </div>

        <table class="services-table">
          <thead>
            <tr>
              <th>المرفق</th>
              <th>إجمالي الخدمات</th>
              <th>الخدمات المتوقفة</th>
              <th>قبل الأزمة</th>
              <th>بعد الأزمة</th>
              <th>نسبة الانخفاض</th>
              <th>مستوى الخطورة</th>
            </tr>
          </thead>

          <tbody>
            <tr v-for="item in paginatedFacilities" :key="item.facility_name">
              <td class="name">{{ item.facility_name }}</td>
              <td>{{ item.total_services }}</td>
              <td>{{ item.interrupted_services }}</td>
              <td>{{ item.pre_visits }}</td>
              <td>{{ item.post_visits }}</td>
              <td>{{ item.drop_rate }}%</td>
              <td>
                <span class="chip" :class="riskClass(item.highest_risk)">
                  {{ item.highest_risk }}
                </span>
              </td>
            </tr>

            <tr v-if="!paginatedFacilities.length">
              <td colspan="7" class="empty">لا توجد بيانات مطابقة</td>
            </tr>
          </tbody>
        </table>

        <div class="pagination-footer">
          <div>
            عرض {{ paginatedFacilities.length }} من
            {{ filteredFacilities.length }}
          </div>

          <div class="pager-controls">
            <select v-model.number="itemsPerPage">
              <option :value="5">5</option>
              <option :value="10">10</option>
              <option :value="20">20</option>
              <option :value="50">50</option>
            </select>

            <button :disabled="currentPage <= 1" @click="currentPage--">
              السابق
            </button>

            <span>{{ currentPage }} / {{ pageCount }}</span>

            <button :disabled="currentPage >= pageCount" @click="currentPage++">
              التالي
            </button>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<script setup>
import { ref, computed, onMounted, watch } from "vue";
import api from "@/plugins/api";

const services = ref([]);
const healthFacilities = ref([]);
const loading = ref(false);

const filters = ref({
  search: "",
  risk: null,
});

const currentPage = ref(1);
const itemsPerPage = ref(10);

const statusLabel = { g: "مطابق", a: "قريب", r: "فجوة" };
const colors = { g: "#10b981", a: "#f59e0b", r: "#ef4444" };

const serviceIndicators = [
  {
    code: "SRV-01",
    name: "معدل التعطل الكلي للخدمات",
    formula: "الخدمات المتوقفة كلياً ÷ إجمالي الخدمات القياسية × 100",
    unit: "%",
    standard: "عتبة التنبيه: ≥25%",
  },
  {
    code: "SRV-02",
    name: "معدل انخفاض الاستخدام",
    formula:
      "(زيارات ما قبل الأزمة − الزيارات الحالية) ÷ زيارات ما قبل الأزمة × 100",
    unit: "%",
    standard: "عتبة التنبيه: ≥20%",
  },
  {
    code: "SRV-03",
    name: "مؤشر صحة الأم والمولود الجديد MNH",
    formula:
      "رعاية ما قبل الولادة 20 + ولادة آمنة 25 + ما بعد الولادة 15 + تنظيم الأسرة 15 + رعاية المواليد 15 + التحصين 10",
    unit: "0–100",
    standard: "شامل ≥80؛ جيد 60–79؛ محدود 40–59؛ حرج <40",
  },
  {
    code: "SRV-04",
    name: "معدل توفر الأدوية الأساسية",
    formula: "فئات الأدوية المتاحة ÷ قائمة الأدوية الأساسية × 100",
    unit: "%",
    standard: "الهدف الوطني: ≥70%",
  },
  {
    code: "SRV-05",
    name: "مؤشر شمولية حزمة الخدمات SPCI",
    formula:
      "(الخدمات العاملة كلياً + 0.5 × العاملة جزئياً) ÷ إجمالي الخدمات القياسية × 100",
    unit: "0–100",
    standard: "كامل ≥80؛ مقبول 60–79؛ حرج <60",
  },
  {
    code: "SRV-06",
    name: "مؤشر الجاهزية للطوارئ الخدمي",
    formula:
      "قسم طوارئ عامل 25 + كادر طوارئ 24/7 20 + معدات أساسية 20 + إسعاف 15 + كهرباء 24/7 10 + مياه متاحة 10",
    unit: "0–100",
    standard: "جاهز كلياً ≥80؛ جزئي 60–79؛ غير جاهز <60",
  },
  {
    code: "SRV-07",
    name: "مؤشر القدرة التشغيلية OCI",
    formula: "إجمالي نقاط التشغيل ÷ الحد الأقصى × 100",
    unit: "%",
    standard: "≥80%",
  },
  {
    code: "SRV-08",
    name: "معدل اضطراب الإمداد",
    formula: "تكرار نفاد الإمدادات لكل ربع سنة",
    unit: "عدد/ربع",
    standard: "<3 لكل ربع",
  },
];

const normalizeArray = (payload) => {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.data)) return payload.data;
  if (Array.isArray(payload?.data?.data)) return payload.data.data;
  if (Array.isArray(payload?.results)) return payload.results;
  return [];
};

const fetchServices = async () => {
  try {
    loading.value = true;

    const [servicesRes, facilitiesRes] = await Promise.all([
      api.get("/facility-services"),
      api.get("/health-facilities"),
    ]);

    const servicesData = normalizeArray(servicesRes.data);
    const facilitiesData = normalizeArray(facilitiesRes.data);

    healthFacilities.value = facilitiesData.map((facility) => ({
      facility_key:
        facility.survey_id ||
        facility.facility_id ||
        facility.health_facility_id ||
        facility.id,

      survey_id: facility.survey_id,
      facility_id: facility.id || facility.facility_id,

      facility_name:
        facility.facility_name_ar ||
        facility.facility_name ||
        facility.name ||
        facility.name_ar ||
        `مرفق #${facility.survey_id || facility.id || "-"}`,
    }));

    services.value = servicesData.map((item) => {
      const serviceKey =
        item.survey_id ||
        item.facility_id ||
        item.health_facility_id ||
        item.facility?.survey_id ||
        item.facility?.id;

      const facility = healthFacilities.value.find(
        (f) =>
          f.facility_key == serviceKey ||
          f.survey_id == item.survey_id ||
          f.facility_id == item.facility_id ||
          f.facility_id == item.health_facility_id
      );

      return {
        id:
          item.id ||
          `${serviceKey}-${
            item.service_code || item.service_id || Math.random()
          }`,

        facility_key: facility?.facility_key || serviceKey,
        survey_id: item.survey_id,

        facility_name:
          facility?.facility_name ||
          item.facility_name ||
          item.facility_name_ar ||
          item.facility?.facility_name_ar ||
          item.survey?.facility?.facility_name_ar ||
          `مرفق #${serviceKey || "-"}`,

        service_code:
          item.service_code ||
          item.ref_service?.service_code ||
          item.service?.service_code ||
          item.service_id ||
          "",

        service_name:
          item.service_name ||
          item.service_name_ar ||
          item.ref_service?.service_name_ar ||
          item.service?.service_name_ar ||
          "خدمة صحية",

        service_status:
          item.service_status ||
          item.status ||
          item.availability_status ||
          item.functionality ||
          "غير محدد",

        pre_crisis_visits: Number(
          item.pre_crisis_visits ||
            item.before_crisis_visits ||
            item.visits_before_crisis ||
            0
        ),

        post_crisis_visits: Number(
          item.post_crisis_visits ||
            item.after_crisis_visits ||
            item.current_visits ||
            item.visits_after_crisis ||
            0
        ),

        risk_level:
          item.risk_level || item.current_risk_level || item.risk || "غير محدد",
      };
    });
  } catch (error) {
    healthFacilities.value = [];
    services.value = [];
    console.error("Facility Services API Error:", error);
  } finally {
    loading.value = false;
  }
};

onMounted(fetchServices);

const isInterrupted = (status) => {
  return [
    "متوقف",
    "لا يعمل",
    "معطل",
    "غير متوفر",
    "out_of_service",
    "stopped",
    "not_available",
  ].includes(status);
};

const riskWeight = {
  حرج: 5,
  مرتفع: 4,
  عالي: 4,
  high: 4,
  متوسط: 3,
  medium: 3,
  منخفض: 2,
  خفيف: 2,
  low: 2,
  "غير محدد": 1,
};

const getHighestRisk = (current, next) => {
  return (riskWeight[next] || 1) > (riskWeight[current] || 1) ? next : current;
};

const facilities = computed(() => {
  const map = {};

  healthFacilities.value.forEach((facility) => {
    const key = facility.facility_key;

    map[key] = {
      facility_key: key,
      facility_name: facility.facility_name,
      total_services: 0,
      interrupted_services: 0,
      pre_visits: 0,
      post_visits: 0,
      highest_risk: "غير محدد",
    };
  });

  services.value.forEach((item) => {
    const key = item.facility_key;

    if (!map[key]) {
      map[key] = {
        facility_key: key,
        facility_name: item.facility_name || "غير محدد",
        total_services: 0,
        interrupted_services: 0,
        pre_visits: 0,
        post_visits: 0,
        highest_risk: "غير محدد",
      };
    }

    map[key].total_services += 1;

    if (isInterrupted(item.service_status)) {
      map[key].interrupted_services += 1;
    }

    map[key].pre_visits += item.pre_crisis_visits;
    map[key].post_visits += item.post_crisis_visits;

    map[key].highest_risk = getHighestRisk(
      map[key].highest_risk,
      item.risk_level
    );
  });

  return Object.values(map).map((item) => {
    const drop =
      item.pre_visits > 0
        ? ((item.pre_visits - item.post_visits) / item.pre_visits) * 100
        : 0;

    return {
      ...item,
      drop_rate: Math.max(0, drop).toFixed(1),
    };
  });
});

const avgDisruptionRate = computed(() => {
  if (!facilities.value.length) return 0;

  const total = facilities.value.reduce((sum, item) => {
    const rate =
      item.total_services > 0
        ? (item.interrupted_services / item.total_services) * 100
        : 0;

    return sum + rate;
  }, 0);

  return Number((total / facilities.value.length).toFixed(1));
});

const avgDropRate = computed(() => {
  if (!facilities.value.length) return 0;

  const total = facilities.value.reduce(
    (sum, item) => sum + Number(item.drop_rate || 0),
    0
  );

  return Number((total / facilities.value.length).toFixed(1));
});

const avgOperationalCapacity = computed(() => {
  if (!facilities.value.length) return 0;

  const total = facilities.value.reduce((sum, item) => {
    const working =
      item.total_services > 0
        ? ((item.total_services - item.interrupted_services) /
            item.total_services) *
          100
        : 0;

    return sum + working;
  }, 0);

  return Number((total / facilities.value.length).toFixed(1));
});

const serviceCharts = computed(() => ({
  "SRV-01": {
    type: "bar",
    horizontal: true,
    label: "معدل تعطل الخدمات لكل مرفق (%)",
    data: facilities.value.map((x) =>
      x.total_services > 0
        ? Number(((x.interrupted_services / x.total_services) * 100).toFixed(1))
        : 0
    ),
    labels: facilities.value.map((x) => x.facility_name || "غير محدد"),
  },
  "SRV-02": {
    type: "bar",
    label: "انخفاض الزيارات حسب المرفق (%)",
    data: facilities.value.map((x) => Number(x.drop_rate || 0)),
    labels: facilities.value.map((x) => x.facility_name || "غير محدد"),
  },
  "SRV-05": {
    type: "bar",
    horizontal: true,
    label: "القدرة التشغيلية حسب المرفق (%)",
    data: facilities.value.map((x) =>
      x.total_services > 0
        ? Number(
            (
              ((x.total_services - x.interrupted_services) / x.total_services) *
              100
            ).toFixed(1)
          )
        : 0
    ),
    labels: facilities.value.map((x) => x.facility_name || "غير محدد"),
  },
  "SRV-07": {
    type: "donut",
    label: "توزيع حالات الخدمات",
    data: statusSeries.value,
    labels: statusLabels.value,
  },
}));

const miniBaseChart = {
  chart: {
    toolbar: { show: false },
    fontFamily: "IBM Plex Sans Arabic, Tahoma, sans-serif",
    foreColor: "#7890ad",
  },
  dataLabels: { enabled: false },
  legend: { position: "bottom" },
  grid: { borderColor: "#d7e0eb" },
  noData: { text: "لا توجد بيانات" },
};

const miniBarOptions = (labels = [], horizontal = false) => ({
  ...miniBaseChart,
  plotOptions: {
    bar: { horizontal, borderRadius: 6, columnWidth: "45%" },
  },
  xaxis: {
    categories: labels.length ? labels : ["لا توجد بيانات"],
    labels: { rotate: horizontal ? 0 : -35, trim: true },
  },
});

const miniDonutOptions = (labels = []) => ({
  ...miniBaseChart,
  labels: labels.length ? labels : ["لا توجد بيانات"],
});

const kpiApex = (kpi) => {
  const ch = kpi.chart;

  if (!ch) {
    return {
      type: "bar",
      series: [],
      options: miniBarOptions([]),
    };
  }

  if (ch.type === "donut") {
    return {
      type: "donut",
      series: ch.data?.length ? ch.data : [0],
      options: miniDonutOptions(ch.labels || []),
    };
  }

  return {
    type: "bar",
    series: [{ name: kpi.code, data: ch.data?.length ? ch.data : [0] }],
    options: miniBarOptions(ch.labels || [], !!ch.horizontal),
  };
};

const serviceKpis = computed(() => [
  {
    code: "SRV-01",
    name: "معدل التعطل الكلي للخدمات",
    val: avgDisruptionRate.value,
    unit: "%",
    std: 25,
    dir: "lt",
    max: 100,
    composite: true,
    flag: avgDisruptionRate.value >= 25,
    chart: serviceCharts.value["SRV-01"],
  },
  {
    code: "SRV-02",
    name: "معدل انخفاض الاستخدام",
    val: avgDropRate.value,
    unit: "%",
    std: 20,
    dir: "lt",
    max: 100,
    composite: false,
    flag: avgDropRate.value >= 20,
    chart: serviceCharts.value["SRV-02"],
  },
  {
    code: "SRV-05",
    name: "مؤشر شمولية حزمة الخدمات",
    val: avgOperationalCapacity.value,
    unit: "/100",
    std: 80,
    dir: "gt",
    max: 100,
    composite: true,
    flag: avgOperationalCapacity.value < 60,
    chart: serviceCharts.value["SRV-05"],
  },
  {
    code: "SRV-07",
    name: "مؤشر القدرة التشغيلية",
    val: avgOperationalCapacity.value,
    unit: "%",
    std: 80,
    dir: "gt",
    max: 100,
    composite: true,
    flag: avgOperationalCapacity.value < 60,
    chart: serviceCharts.value["SRV-07"],
  },
]);

const kpiStatus = (kpi) => {
  const v = Number(kpi.val || 0);
  const std = Number(kpi.std || 0);

  if (kpi.dir === "lt") {
    if (v <= std) return "g";
    if (v <= std * 1.2) return "a";
    return "r";
  }

  if (v >= std) return "g";
  if (v >= std * 0.75) return "a";
  return "r";
};

const statusColor = (kpi) => colors[kpiStatus(kpi)];

const achieve = (kpi) => {
  const v = Number(kpi.val || 0);
  const std = Number(kpi.std || 1);
  const max = Number(kpi.max || 100);

  if (kpi.dir === "lt") {
    return v <= std
      ? 100
      : Math.max(
          0,
          Math.round(100 - ((v - std) / Math.max(1, max - std)) * 100)
        );
  }

  return Math.min(100, Math.round((v / std) * 100));
};

const barWidth = (kpi) =>
  Math.min(
    100,
    Math.max(3, (Number(kpi.val || 0) / Number(kpi.max || 100)) * 100)
  );

const serviceCriticalCount = computed(
  () => serviceKpis.value.filter((k) => k.flag && kpiStatus(k) !== "g").length
);

const countBy = (rows, field) => {
  const result = {};

  rows.forEach((item) => {
    const value = item[field] || "غير محدد";
    result[value] = (result[value] || 0) + 1;
  });

  return result;
};

const riskLevelData = computed(() => countBy(facilities.value, "highest_risk"));
const riskLevelLabels = computed(() => Object.keys(riskLevelData.value));
const riskLevelSeries = computed(() => Object.values(riskLevelData.value));

const statusData = computed(() => countBy(services.value, "service_status"));
const statusLabels = computed(() => Object.keys(statusData.value));
const statusSeries = computed(() => Object.values(statusData.value));

const facilityVisitsSeries = computed(() => [
  {
    name: "قبل الأزمة",
    data: facilities.value.map((item) => item.pre_visits),
  },
  {
    name: "بعد الأزمة",
    data: facilities.value.map((item) => item.post_visits),
  },
]);

const riskItems = computed(() => [
  ...new Set(facilities.value.map((x) => x.highest_risk).filter(Boolean)),
]);

const filteredFacilities = computed(() => {
  return facilities.value.filter((item) => {
    const search = filters.value.search?.trim()?.toLowerCase();

    if (search && !item.facility_name.toLowerCase().includes(search)) {
      return false;
    }

    if (filters.value.risk && item.highest_risk !== filters.value.risk) {
      return false;
    }

    return true;
  });
});

const pageCount = computed(() =>
  Math.max(1, Math.ceil(filteredFacilities.value.length / itemsPerPage.value))
);

const paginatedFacilities = computed(() => {
  const start = (currentPage.value - 1) * itemsPerPage.value;
  return filteredFacilities.value.slice(start, start + itemsPerPage.value);
});

watch(
  () => [filters.value.search, filters.value.risk, itemsPerPage.value],
  () => {
    currentPage.value = 1;
  }
);

const riskClass = (value) => {
  if (["منخفض", "خفيف", "low"].includes(value)) return "green";
  if (["متوسط", "medium"].includes(value)) return "orange";
  if (value === "غير محدد") return "gray";
  return "red";
};

const donutOptions = (labels = []) => ({
  chart: {
    type: "donut",
    toolbar: { show: false },
    fontFamily: "IBM Plex Sans Arabic, Tahoma, sans-serif",
  },
  labels: labels.length ? labels : ["غير محدد"],
  legend: { position: "bottom" },
  dataLabels: { enabled: true },
  noData: { text: "لا توجد بيانات" },
});

const barOptions = (labels = []) => ({
  chart: {
    type: "bar",
    toolbar: { show: false },
    fontFamily: "IBM Plex Sans Arabic, Tahoma, sans-serif",
  },
  xaxis: {
    categories: labels.length ? labels : ["غير محدد"],
    labels: { rotate: -45 },
  },
  plotOptions: {
    bar: {
      horizontal: false,
      borderRadius: 6,
    },
  },
  dataLabels: { enabled: false },
  noData: { text: "لا توجد بيانات" },
});
</script>

<style scoped>
.service-page {
  min-height: 100vh;
  background: #f4f6f9;
  padding: 24px;
  color: #071126;
  font-family: "IBM Plex Sans Arabic", Tahoma, sans-serif;
}

.page-loading {
  position: fixed;
  inset: 0;
  background: rgba(244, 246, 249, 0.92);
  backdrop-filter: blur(6px);
  z-index: 9999;
  display: flex;
  align-items: center;
  justify-content: center;
}

.loader-card {
  width: 320px;
  background: #fff;
  border: 1px solid #dbe3ee;
  border-radius: 22px;
  padding: 34px 24px;
  text-align: center;
  box-shadow: 0 20px 60px rgba(15, 35, 64, 0.15);
}

.loader-ring {
  width: 64px;
  height: 64px;
  border: 6px solid #e2e8f0;
  border-top-color: #0f766e;
  border-radius: 50%;
  margin: 0 auto 18px;
  animation: spin 0.8s linear infinite;
}

.loader-card h3 {
  font-size: 18px;
  font-weight: 900;
  color: #0f766e;
  margin-bottom: 8px;
}

.loader-card p {
  color: #64748b;
  font-size: 13px;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

.page-header {
  text-align: right;
  margin-bottom: 26px;
}

.page-header h1 {
  font-size: 28px;
  font-weight: 900;
  margin-bottom: 8px;
  color: #061226;
}

.page-header p {
  color: #52657d;
  font-size: 14px;
}

.top-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 18px;
}

.counter {
  color: #64748b;
  font-size: 14px;
}

.top-row select,
.filter-box select,
.filter-box input,
.pager-controls select {
  height: 50px;
  background: #fff;
  border: 1px solid #cbd5e1;
  border-radius: 8px;
  padding: 0 14px;
  font-family: inherit;
  outline: none;
}

.top-row select {
  width: 225px;
}

.axis-summary {
  background: #fff;
  border: 1px solid #dbe3ee;
  border-radius: 18px;
  padding: 18px;
  margin-bottom: 18px;
  box-shadow: 0 8px 24px rgba(15, 35, 64, 0.05);
}

.axis-card-header {
  display: flex;
  justify-content: space-between;
  gap: 20px;
  align-items: center;
}

.axis-card-header h2 {
  font-size: 22px;
  font-weight: 900;
  color: #0f766e;
  margin-bottom: 6px;
}

.axis-card-header p {
  color: #64748b;
  font-size: 13px;
  margin-bottom: 8px;
}

.axis-card-header strong {
  color: #071126;
  font-size: 14px;
}

.axis-score {
  min-width: 130px;
  text-align: center;
  background: #ecfdf5;
  color: #0f766e;
  border-radius: 18px;
  padding: 18px;
}

.axis-score span {
  display: block;
  font-size: 34px;
  font-weight: 900;
}

.axis-score small {
  font-weight: 800;
}

.axis-stats {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 12px;
  margin-top: 16px;
}

.axis-stats div {
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 14px;
  padding: 14px;
  text-align: center;
}

.axis-stats b {
  display: block;
  font-size: 26px;
  color: #0f766e;
}

.axis-stats span {
  font-size: 13px;
  font-weight: 800;
  color: #64748b;
}

.service-kpi-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));
  gap: 14px;
  margin-bottom: 18px;
}

.service-kpi-card {
  background: #fff;
  border: 1px solid #dbe3ee;
  border-radius: 16px;
  padding: 16px;
  position: relative;
  box-shadow: 0 10px 28px rgba(15, 23, 42, 0.06);
}

.service-kpi-card.flag {
  border-color: rgba(239, 68, 68, 0.38);
}

.service-kpi-card.flag::after {
  content: "⚑ مؤشر حرج";
  position: absolute;
  top: 13px;
  left: 14px;
  font-size: 10px;
  color: #ef4444;
  font-weight: 900;
}

.service-kpi-top {
  display: flex;
  gap: 8px;
  margin-bottom: 10px;
}

.composite {
  background: #f5f3ff;
  border: 1px solid #ddd6fe;
  color: #7c3aed;
  border-radius: 7px;
  padding: 3px 8px;
  font-size: 10px;
  font-weight: 900;
}

.service-kpi-card h3 {
  font-size: 15px;
  font-weight: 900;
  color: #071126;
  margin: 0 0 12px;
  min-height: 42px;
}

.service-kpi-value {
  display: flex;
  align-items: baseline;
  gap: 7px;
  margin-bottom: 10px;
}

.service-kpi-value strong {
  font-size: 32px;
  font-weight: 900;
  direction: ltr;
}

.service-kpi-value span {
  color: #64748b;
}

.service-kpi-value em {
  margin-right: auto;
  font-style: normal;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  padding: 5px 9px;
  color: #475569;
  font-size: 11px;
  font-weight: 800;
}

.gauge {
  height: 9px;
  background: #e2e8f0;
  border-radius: 999px;
  overflow: hidden;
}

.gauge div {
  height: 100%;
  border-radius: 999px;
}

.service-kpi-foot {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin: 8px 0 0;
  font-size: 12px;
  font-weight: 700;
  color: #64748b;
}

.rag {
  border-radius: 7px;
  padding: 4px 10px;
  font-weight: 900;
}

.rag.g {
  background: rgba(16, 185, 129, 0.12);
  color: #10b981;
}

.rag.a {
  background: rgba(245, 158, 11, 0.14);
  color: #f59e0b;
}

.rag.r {
  background: rgba(239, 68, 68, 0.12);
  color: #ef4444;
}

.axis-card {
  background: #fff;
  border: 1px solid #dbe3ee;
  border-radius: 12px;
  padding: 22px;
  box-shadow: 0 1px 4px rgba(15, 35, 64, 0.05);
}

.section-title {
  font-size: 17px;
  font-weight: 900;
  color: #0f766e;
  margin-bottom: 12px;
}

.charts-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 14px;
  margin-bottom: 16px;
}

.chart-card,
.services-table-wrap {
  background: #f8fafc;
  border: 1px solid #dbe3ee;
  border-radius: 12px;
}

.chart-card {
  padding: 16px;
  min-height: 370px;
}

.empty-chart,
.empty {
  text-align: center;
  color: #64748b;
  font-weight: 800;
  padding: 26px;
}

.filter-box {
  display: grid;
  grid-template-columns: 1.5fr 1fr;
  gap: 12px;
  margin-bottom: 16px;
}

.services-table-wrap {
  margin-bottom: 18px;
  overflow-x: auto;
}

.table-title {
  background: #0f766e;
  color: #fff;
  padding: 14px 18px;
  margin-bottom: 0;
  display: flex;
  justify-content: space-between;
  font-size: 17px;
  font-weight: 900;
}

.table-title span {
  background: rgba(255, 255, 255, 0.15);
  padding: 4px 10px;
  border-radius: 999px;
  font-size: 12px;
}

.services-table,
.table-wrap table {
  width: 100%;
  min-width: 100%;
  border-collapse: collapse;
  background: #fff;
}

.services-table th {
  background: #f8fafc;
  color: #0f766e;
  font-weight: 900;
  padding: 13px;
  border-bottom: 1px solid #e2e8f0;
  text-align: right;
  white-space: nowrap;
}

.services-table td {
  padding: 13px;
  border-bottom: 1px solid #eef2f7;
  font-size: 13px;
  white-space: nowrap;
}

.pagination-footer {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 14px 18px;
  color: #64748b;
  flex-wrap: wrap;
  gap: 12px;
}

.pager-controls {
  display: flex;
  align-items: center;
  gap: 8px;
}

.pager-controls button {
  height: 38px;
  border: none;
  border-radius: 8px;
  padding: 0 14px;
  background: #0f766e;
  color: #fff;
  cursor: pointer;
  font-family: inherit;
}

.pager-controls button:disabled {
  background: #cbd5e1;
  cursor: not-allowed;
}

.chip {
  display: inline-flex;
  padding: 6px 12px;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 800;
}

.chip.green {
  background: #e8f5e9;
  color: #1b5e20;
}

.chip.orange {
  background: #fff4e5;
  color: #d4821a;
}

.chip.red {
  background: #fdecea;
  color: #c62828;
}

.chip.gray {
  background: #f1f5f9;
  color: #475569;
}

.table-wrap {
  overflow-x: auto;
  border: 1px solid #dbe3ee;
  border-radius: 10px;
  margin-bottom: 18px;
  background: #fff;
}

.table-wrap table {
  min-width: 1200px;
}

.indicators-table table {
  min-width: 1300px;
}

.table-wrap th {
  background: #f8fafc;
  color: #0f766e;
  font-size: 13px;
  font-weight: 900;
  padding: 15px 14px;
  text-align: right;
  white-space: nowrap;
  border-bottom: 1px solid #e2e8f0;
}

.table-wrap td {
  padding: 14px;
  border-bottom: 1px solid #dbe3ee;
  font-size: 13px;
  line-height: 1.8;
  vertical-align: top;
}

tbody tr:hover {
  background: #f8fafc;
}

.code {
  font-weight: 900;
  direction: ltr;
  text-align: center;
  white-space: nowrap;
  color: #0f766e;
  background: #ecfdf5;
  border: 1px solid #bbf7d0;
  border-radius: 7px;
  padding: 3px 8px;
  font-size: 11px;
}

.name {
  font-weight: 900;
  min-width: 220px;
}

.unit {
  text-align: center;
  white-space: nowrap;
}

@media (max-width: 1100px) {
  .charts-grid,
  .filter-box,
  .axis-stats {
    grid-template-columns: 1fr;
  }

  .axis-card-header {
    flex-direction: column;
    align-items: stretch;
  }
}

@media (max-width: 900px) {
  .service-page {
    padding: 16px 12px;
  }

  .page-header h1 {
    font-size: 21px;
  }

  .top-row {
    flex-direction: column-reverse;
    align-items: stretch;
    gap: 12px;
  }

  .top-row select {
    width: 100%;
  }

  .axis-card {
    padding: 14px;
  }
}

/* ===== Indicator mini cards copied from axis indicators page ===== */
.axis-indicator-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));
  gap: 14px;
  margin-bottom: 18px;
}

.axis-indicator-card {
  background: #fff;
  border: 1px solid #dbe3ee;
  border-radius: 16px;
  padding: 16px;
  position: relative;
  box-shadow: 0 10px 28px rgba(15, 23, 42, 0.06);
}

.axis-indicator-card.flag {
  border-color: rgba(239, 68, 68, 0.38);
}

.axis-indicator-card.flag::after {
  content: "⚑ مؤشر حرج";
  position: absolute;
  top: 13px;
  left: 14px;
  font-size: 10px;
  color: #ef4444;
  font-weight: 900;
}

.axis-indicator-top {
  display: flex;
  gap: 8px;
  margin-bottom: 10px;
}

.axis-indicator-card h3 {
  font-size: 15px;
  font-weight: 900;
  color: #071126;
  margin: 0 0 12px;
  min-height: 42px;
}

.axis-indicator-value {
  display: flex;
  align-items: baseline;
  gap: 7px;
  margin-bottom: 10px;
}

.axis-indicator-value strong {
  font-size: 32px;
  font-weight: 900;
  direction: ltr;
}

.axis-indicator-value span {
  color: #64748b;
}

.axis-indicator-value em {
  margin-right: auto;
  font-style: normal;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  padding: 5px 9px;
  color: #475569;
  font-size: 11px;
  font-weight: 800;
}

.axis-indicator-foot {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin: 8px 0 13px;
  font-size: 12px;
  font-weight: 700;
  color: #64748b;
}

.axis-mini-chart {
  background: #f8fafc;
  border: 1px solid #dbe3ee;
  border-radius: 14px;
  padding: 14px;
}

.axis-mini-chart h4 {
  font-size: 14px;
  font-weight: 900;
  color: inherit;
  margin: 0 0 10px;
}

.composite {
  background: #f5f3ff;
  border: 1px solid #ddd6fe;
  color: #7c3aed;
  border-radius: 7px;
  padding: 3px 8px;
  font-size: 10px;
  font-weight: 900;
}

.gauge {
  height: 9px;
  background: #e2e8f0;
  border-radius: 999px;
  overflow: hidden;
}

.gauge div {
  height: 100%;
  border-radius: 999px;
}

.rag {
  border-radius: 7px;
  padding: 4px 10px;
  font-weight: 900;
}

.rag.g {
  background: rgba(16, 185, 129, 0.12);
  color: #10b981;
}

.rag.a {
  background: rgba(245, 158, 11, 0.14);
  color: #f59e0b;
}

.rag.r {
  background: rgba(239, 68, 68, 0.12);
  color: #ef4444;
}
</style>
